import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { parseISO } from 'date-fns';
import { format, formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { DatePicker, Modal, Text, message, breakpoints } from 'components';
import { makeModalResponsive, ModalType } from 'utils';
import TourEditContext from '../TourEditContext';
import { getStartOfDayInTimezone } from '../utils';
import { TourItemContent } from '../types';

const showCannotSelectDateModal = () => {
  makeModalResponsive(
    Modal.error({
      title: 'Date cannot be changed',
      content: (
        <Text type="secondary">
          You cannot change the date for tours that have submitted showing
          requests. In order to select a new date, please cancel this tour and
          create a new one.
        </Text>
      ),
      centered: true,
      getContainer: document.getElementById('tour-edit-page-root'),
    }),
    {
      modalType: ModalType.INFO,
      dataCys: { content: 'date-cannot-change-modal' },
    },
  );
};

const TourDateSelector: React.FC = () => {
  // context hooks
  const { auth } = useContext(AuthContext);
  const { tour, setTour, tourItems, setTourItems } =
    useContext(TourEditContext);

  // state hooks
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // custom hooks
  const { md } = useBreakpoint();

  // consts
  const allTourItemsAreDraft = useMemo(
    () => !tourItems.some(tourItem => Boolean(tourItem.appointmentId)),
    [tourItems],
  );

  const userTimezone =
    auth.org.addresses[parseInt(auth.location, 10)][parseInt(auth.address, 10)]
      .timezone;

  const tourTimezone =
    tourItems.find(tourItem => Boolean(tourItem.timezone))?.timezone ||
    userTimezone;

  const normalizedDateForPicker = useMemo(() => {
    const todayDateStr = formatInTimeZone(
      new Date(),
      tourTimezone,
      'yyyy-MM-dd',
    );

    const tourDateStr = formatInTimeZone(
      tour.date || new Date(),
      tourTimezone,
      'yyyy-MM-dd',
    );

    // For draft tours, don't show a date earlier than today.
    const dateStrForPicker =
      allTourItemsAreDraft && tourDateStr < todayDateStr
        ? todayDateStr
        : tourDateStr;

    // The day string above is already computed in tourTimezone.
    // Parse it as a date-only local value for DatePicker display so the
    // selected calendar day does not shift.
    return parseISO(dateStrForPicker);
  }, [tour.date, tourTimezone, allTourItemsAreDraft]);

  // effect hooks
  // make pickerOpen depend on modalOpen, or else the two antd components race to open first,
  // which produces janky UX
  useEffect(() => {
    if (modalOpen) {
      setPickerOpen(true);
    } else {
      setPickerOpen(false);
    }
  }, [modalOpen]);

  // functions
  const disabledDate = (current: Date) =>
    // Can not select days before today
    current &&
    current.getTime() <
      getStartOfDayInTimezone(new Date(), tourTimezone).getTime();
  const handleDateChange = (date: Date) => {
    const selectedDateStr = format(date, 'yyyy-MM-dd');

    setTour(prev => ({
      ...prev,
      date: zonedTimeToUtc(parseISO(selectedDateStr), tourTimezone),
    }));

    if (tourItems.some(tourItem => Boolean(tourItem.time))) {
      message.info('Please select new times for your showings');
    }

    if (!md) setModalOpen(false);

    setTourItems(curTourItems => {
      const newState: TourItemContent[] = [];
      curTourItems.forEach(tourItem_ => {
        newState.push({ ...tourItem_, timeEnd: undefined, time: undefined });
      });
      return newState;
    });
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  if (md) {
    return (
      <StyledDateInput
        data-cy="tours-input-date"
        disabledDate={disabledDate}
        onChange={handleDateChange}
        value={normalizedDateForPicker}
        format={'EEE MMM d'}
        disabled={!allTourItemsAreDraft}
        showToday={false}
        $md={md}
        onClick={() => {
          if (!allTourItemsAreDraft) {
            showCannotSelectDateModal();
          }
        }}
      />
    );
  }

  return (
    <>
      <StyledModal
        title="Pick a date"
        open={modalOpen}
        bodyStyle={{ padding: 0 }}
        footer={null}
        width={breakpoints.md}
        onCancel={handleCancel}
        transitionName="ant-move-down"
      >
        <div id="tour-mobile-datepicker"></div>
      </StyledModal>
      <StyledDateInput
        data-cy="tours-input-date"
        disabledDate={disabledDate}
        onChange={handleDateChange}
        value={normalizedDateForPicker}
        format={'EEE MMM d'}
        disabled={!allTourItemsAreDraft}
        showToday={false}
        $md={md}
        getPopupContainer={() =>
          document.getElementById('tour-mobile-datepicker')
        }
        open={pickerOpen}
        onClick={() => {
          if (!allTourItemsAreDraft) {
            showCannotSelectDateModal();
          } else {
            setModalOpen(true);
          }
        }}
      />
    </>
  );
};

export default TourDateSelector;

const StyledDateInput = styled(DatePicker)<{ $md: boolean }>`
  margin: ${({ $md }) => ($md ? '16px 24px 0px 24px' : '16px 12px 0px 12px')};
`;

// Don't like using !important, but recommended by styled-components to override third party internal inline styles,
// where increasing specificity with &&& {} wont work
// https://styled-components.com/docs/faqs#how-can-i-override-inline-styles
const StyledModal = styled(Modal)`
  position: fixed;
  top: unset;
  max-width: 100vw;
  bottom: 0;
  right: 0;
  left: 0;
  margin: 0;
  padding-bottom: 0;

  #tour-mobile-datepicker > div {
    &[style] {
      position: static !important;
    }

    > div > .ant-picker-dropdown {
      &[style] {
        top: 0 !important;
        left: 0 !important;
        position: relative !important;
      }
    }

    .ant-picker-panel-container {
      box-shadow: none;
    }
    .ant-picker-panel {
      width: 100%;
      border: none;
    }
    .ant-picker-date-panel {
      width: 100%;
    }
    .ant-picker-content {
      margin: 0 auto;
    }
  }
`;
